import { Box, Typography, IconButton } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { formatTimeAgo, getDataFreshnessColor } from '../../utils/timeFormatters';
import { SxProps, Theme } from '@mui/material';

interface RefreshBarProps {
  lastUpdated: Date;
  loading: boolean;
  onRefresh: () => void;
}

// Styles
const refreshBarContainerStyles: SxProps<Theme> = {
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: 'background.paper',
  borderTop: 1,
  borderColor: 'divider',
  zIndex: 1100
};

const refreshBarContentStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  height: { xs: 36, sm: 40 },
  px: { xs: 2, sm: 3 },
  maxWidth: '100%',
  mx: 'auto'
};

const refreshBarLeftSideStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minWidth: 0,
  flex: '0 1 auto'
};

const getRefreshStatusDotStyles = (freshnessColor: string): SxProps<Theme> => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: `${freshnessColor}.main`,
  flexShrink: 0
});

const refreshBarTextStyles: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: { xs: '0.75rem', sm: '0.875rem' },
  whiteSpace: 'nowrap'
};

const refreshButtonStyles: SxProps<Theme> = {
  color: 'primary.main',
  '&:hover': {
    backgroundColor: 'action.hover'
  }
};

const refreshIconStyles: SxProps<Theme> = {
  fontSize: 20
};

/**
 * RefreshBar - Displays last refresh time and refresh button
 *
 * Shows a fixed bar at the bottom of the page with:
 * - Colored status dot indicating data freshness
 * - Last refreshed time
 * - Refresh button
 */
export function RefreshBar({ lastUpdated, loading, onRefresh }: RefreshBarProps) {
  return (
    <Box sx={refreshBarContainerStyles}>
      <Box sx={refreshBarContentStyles}>
        {/* Left side - Status and last refreshed */}
        <Box sx={refreshBarLeftSideStyles}>
          {/* Colored status dot */}
          <Box
            sx={getRefreshStatusDotStyles(getDataFreshnessColor(lastUpdated))}
          />

          {/* Last refreshed text */}
          <Typography
            variant="body2"
            sx={refreshBarTextStyles}
          >
            Last refreshed: {formatTimeAgo(lastUpdated)}
          </Typography>
        </Box>

        {/* Right side - Refresh button */}
        {!loading && (
          <IconButton
            size="small"
            onClick={onRefresh}
            sx={refreshButtonStyles}
            title="Refresh now"
          >
            <RefreshIcon sx={refreshIconStyles} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
